import {Injectable} from '@angular/core';

/**
 * Result of classifying a user's subscription for ForU+ routing.
 *  - 'none'     => no active subscription; eligible to enroll on /foruplus
 *  - 'legacy'   => active legacy FreshPass subscription; keep on legacy manage flow
 *  - 'foruplus'  => active ForU+ subscription; belongs on /foruplus/manage
 */
export type ProgramClassification = 'legacy' | 'foruplus' | 'none';

/**
 * Single source of truth for deciding which program a user belongs to.
 *
 * Consumes the payload returned by SubscriptionService.getUserSubscriptionDetails()
 * (the `Data` object: { subscriptionStatus, subscriptionCode, planGeneration, ... }).
 *
 * The `planGeneration` marker on the getSubscription payload is the source of truth for which
 * program a subscriber belongs to. It is compared lower-cased ('b4u_plus' => ForU+, 'legacy' =>
 * legacy FreshPass). If a subscription is active but carries no recognized planGeneration we
 * default to 'legacy' — the safe fallback that never strands an existing subscriber.
 */
@Injectable({
    providedIn: 'root'
})
export class ProgramClassifier {

    // ForU+ launch: finalized backend markers on the getSubscription `planGeneration` field.
    private readonly FORUPLUS_PLAN_GENERATION = 'b4u_plus';
    private readonly LEGACY_PLAN_GENERATION = 'legacy';

    private readonly CANCELLED = 'CANCELLED';

    classify(subscriptionDetails: any): ProgramClassification {
        if (!subscriptionDetails) {
            return 'none';
        }
        const planGeneration = (subscriptionDetails.planGeneration || '').toString().toLowerCase();
        if (planGeneration === this.FORUPLUS_PLAN_GENERATION) {
            return 'foruplus';
        }
        if (planGeneration === this.LEGACY_PLAN_GENERATION) {
            return 'legacy';
        }
        // No recognized planGeneration. If there is still an active (non-cancelled) subscription,
        // default to 'legacy' so an existing subscriber is never stranded on the enrollment page.
        const status = this.statusOf(subscriptionDetails);
        if (status && !this.isCancelled(subscriptionDetails)) {
            return 'legacy';
        }
        // No active subscription (missing status or cancelled) => eligible to enroll.
        return 'none';
    }

    /**
     * Whether the subscription is cancelled. The CANCELLED marker arrives on `subscriptionStatus`
     * on the getSubscription payload, but the legacy manage flow keyed off a top-level `status`
     * field; accept either so a cancelled subscriber is recognized regardless of which the
     * payload carries. Compared case-insensitively (the backend sends upper-case).
     */
    isCancelled(subscriptionDetails: any): boolean {
        return this.statusOf(subscriptionDetails).toUpperCase() === this.CANCELLED;
    }

    private statusOf(subscriptionDetails: any): string {
        return (subscriptionDetails?.subscriptionStatus || subscriptionDetails?.status || '').toString();
    }
}
